import { useState, useCallback, useMemo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSearchParams } from 'react-router-dom'
import {
  DeprecProdUnit,
  ProductionUnit,
  productionUnitSchema,
  FetchProductionUnitsParams,
  CreateProductionUnitParams,
  UpdateProductionUnitParams,
} from '@api/models'
import {
  useFetchProductionUnitsQuery,
  useCreateProductionUnitMutation,
  useUpdateProductionUnitMutation,
  useDeleteProductionUnitMutation,
  useGetCompaniesQuery,
} from '@api'
import { toast } from '@common'
import { ReactSelectOptions } from '@components/common/Select/Select.types'
import { useSelector } from 'react-redux'

interface UseProductionUnitsProps {
  params: FetchProductionUnitsParams
}

function wasYearFullyFilled(units: DeprecProdUnit[], year: number, periodsPerYear: number) {
  const lastUnitIsOnLastPeriodAvailable =
    units[units.length - 1].FiscalPeriod === periodsPerYear ? true : false
  return lastUnitIsOnLastPeriodAvailable
}

function hasSkippedPeriods(
  newYear: number,
  newPeriod: number,
  allUnits: DeprecProdUnit[],
  periodsPerYear: number
): boolean {
  // 1) Check skip within the same year (e.g. 7,8,9 → try 12)
  const existingForYear = allUnits
    .filter(u => u.FiscalYear === newYear)
    .map(u => u.FiscalPeriod)

  if (existingForYear[existingForYear.length - 1] + 1 === newPeriod) {
    return false
  }

  if (existingForYear.length === 0 && newPeriod !== 1) {
    return true
  }

  // 2) Check if previous year is incomplete
  const previousYear = newYear - 1
  const previousYearComplete = wasYearFullyFilled(allUnits, previousYear, periodsPerYear)

  if (!previousYearComplete && newYear > previousYear) {
    return true
  }

  // 3) Jump multiple years (always skip)
  const earliestExistingYear = Math.min(...allUnits.map(u => u.FiscalYear))
  console.log('earliestExistingYear', earliestExistingYear)
  if (newYear > earliestExistingYear + 1) {
    return true
  }

  return false
}

export const useProductionUnits = ({ params }: UseProductionUnitsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const searchParamsObject = Object.fromEntries([...searchParams])
  const { isLoading, data } = useGetCompaniesQuery()
  const assetCompanyID = useSelector<any>(state => state.company)
  const assetCompany = data?.find(company => company.AssetCompanyID === assetCompanyID)
  const periodsPerYear = assetCompany?.CM_NR_PER

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [formType, setFormType] = useState<'add' | 'edit'>('add')
  const [selectedUnit, setSelectedUnit] = useState<DeprecProdUnit | null>(null)

  const [isSkippedPeriodModalOpen, setIsSkippedPeriodModalOpen] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState<ProductionUnit | null>(null)

  const [getFirstAvailablePeriodCallback, setGetFirstAvailablePeriodCallback] = useState<
    ((year: number) => number | null) | null
  >(null)
  const [getFirstAvailableYearCallback, setGetFirstAvailableYearCallback] = useState<
    (() => number | null) | null
  >(null)

  const {
    data: allProductionUnits = [],
    isLoading: isLoadingUnits,
    error: fetchError,
  } = useFetchProductionUnitsQuery(params)

  const availableYears = useMemo(() => {
    const years = [...new Set(allProductionUnits.map(unit => unit.FiscalYear))]
    return years.sort((a, b) => b - a)
  }, [allProductionUnits])

  const selectedYear = searchParams.get('year') ? parseInt(searchParams.get('year')) : null

  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSearchParams({
        ...searchParamsObject,
        year: availableYears[0].toString(),
      })
    }
  }, [availableYears, selectedYear, searchParamsObject, setSearchParams])

  const productionUnits = useMemo(() => {
    if (!selectedYear) return allProductionUnits
    return allProductionUnits.filter(unit => unit.FiscalYear === selectedYear)
  }, [allProductionUnits, selectedYear])

  const yearOptions: ReactSelectOptions = useMemo(() => {
    return availableYears.map(year => ({
      label: `Fiscal year: ${year}`,
      value: year,
    }))
  }, [availableYears])

  const handleChangeYear = useCallback(
    (e: { label: string; value: any }) => {
      setSearchParams({
        ...searchParamsObject,
        year: e.value.toString(),
      })
    },
    [searchParamsObject, setSearchParams]
  )

  const [createUnit, { isLoading: isCreating }] = useCreateProductionUnitMutation()
  const [updateUnit, { isLoading: isUpdating }] = useUpdateProductionUnitMutation()
  const [deleteUnit, { isLoading: isDeleting }] = useDeleteProductionUnitMutation()

  const formMethods = useForm<ProductionUnit>({
    resolver: yupResolver(productionUnitSchema),
    mode: 'onChange',
  })

  const deleteFormMethods = useForm({
    mode: 'onChange',
  })

  const openAddForm = useCallback(() => {
    setFormType('add')
    setSelectedUnit(null)
    formMethods.reset({
      AssetCompanyID: params.assetCompanyID,
      AssetID: params.assetID,
      FiscalYear: undefined,
      FiscalPeriod: undefined,
      PeriodEndDate: '',
      UnitsProduced: 0,
    })
    setIsFormOpen(true)
  }, [formMethods, params])

  const openEditForm = useCallback(
    (unit: DeprecProdUnit) => {
      setFormType('edit')
      setSelectedUnit(unit)
      formMethods.reset({
        AssetCompanyID: unit.AssetCompanyID,
        AssetID: unit.AssetID,
        FiscalYear: unit.FiscalYear,
        FiscalPeriod: unit.FiscalPeriod,
        PeriodEndDate: unit.PeriodEndDate,
        UnitsProduced: unit.UnitsProduced,
      })
      setIsFormOpen(true)
    },
    [formMethods]
  )

  const openDeleteModal = useCallback((unit: DeprecProdUnit) => {
    setSelectedUnit(unit)
    setIsDeleteModalOpen(true)
  }, [])

  const closeForm = useCallback(() => {
    setIsFormOpen(false)
    setIsDeleteModalOpen(false)
    setSelectedUnit(null)
    formMethods.reset()
  }, [formMethods])

  const handleSubmit = useCallback(
    async (data: ProductionUnit) => {
      try {
        if (formType === 'add') {
          let firstAvailablePeriod: number | null = null
          let firstAvailableYear: number | null = null

          if (getFirstAvailablePeriodCallback) {
            firstAvailablePeriod = getFirstAvailablePeriodCallback(data.FiscalYear)
          } else {
            console.warn(
              'getFirstAvailablePeriod callback not provided, skipping period validation'
            )
          }

          if (getFirstAvailableYearCallback) {
            firstAvailableYear = getFirstAvailableYearCallback()
          } else {
            console.warn(
              'getFirstAvailableYear callback not provided, skipping year validation'
            )
          }

          const hasSkipped = hasSkippedPeriods(
            data.FiscalYear,
            data.FiscalPeriod,
            allProductionUnits,
            periodsPerYear
          )

          if (hasSkipped) {
            setPendingSubmitData(data)
            setIsSkippedPeriodModalOpen(true)
            return
          }
        }

        await submitProductionUnit(data)
      } catch (error: any) {
        toast.error(error?.data || 'There was an error submitting the production unit.')
      }
    },
    [formType, getFirstAvailablePeriodCallback, getFirstAvailableYearCallback]
  )

  const submitProductionUnit = useCallback(
    async (data: ProductionUnit) => {
      if (formType === 'add') {
        const createParams: CreateProductionUnitParams = {
          AssetCompanyID: data.AssetCompanyID,
          AssetID: data.AssetID,
          FiscalYear: data.FiscalYear,
          FiscalPeriod: data.FiscalPeriod,
          PeriodEndDate: data.PeriodEndDate,
          UnitsProduced: data.UnitsProduced,
        }
        await createUnit(createParams).unwrap()
        toast.success('Production unit added successfully.')
      } else if (selectedUnit) {
        const updateParams: UpdateProductionUnitParams = {
          AppCompanyID: selectedUnit.AppCompanyID,
          AssetCompanyID: data.AssetCompanyID,
          AssetID: data.AssetID,
          FiscalYear: data.FiscalYear,
          FiscalPeriod: data.FiscalPeriod,
          PeriodEndDate: data.PeriodEndDate,
          UnitsProduced: data.UnitsProduced,
        }
        await updateUnit(updateParams).unwrap()
        toast.success('Production unit updated successfully.')
      }
      closeForm()
    },
    [formType, selectedUnit, createUnit, updateUnit, closeForm]
  )

  const handleConfirmedSubmit = useCallback(async () => {
    if (!pendingSubmitData) return

    try {
      await submitProductionUnit(pendingSubmitData)

      setIsSkippedPeriodModalOpen(false)
      setPendingSubmitData(null)
    } catch (error: any) {
      toast.error(error?.data || 'There was an error creating the production unit.')
    }
  }, [pendingSubmitData, submitProductionUnit])

  const closeSkippedPeriodModal = useCallback(() => {
    setIsSkippedPeriodModalOpen(false)
    setPendingSubmitData(null)
  }, [])

  const setFirstAvailablePeriodCallback = useCallback(
    (callback: (year: number) => number | null) => {
      setGetFirstAvailablePeriodCallback(() => callback)
    },
    []
  )

  const setFirstAvailableYearCallback = useCallback((callback: () => number | null) => {
    setGetFirstAvailableYearCallback(() => callback)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!selectedUnit) return

    try {
      await deleteUnit({
        appCompanyID: selectedUnit.AppCompanyID,
        assetCompanyID: selectedUnit.AssetCompanyID,
        assetID: selectedUnit.AssetID,
        fiscalYear: selectedUnit.FiscalYear,
        fiscalPeriod: selectedUnit.FiscalPeriod,
      }).unwrap()
      closeForm()
    } catch (error: any) {
      console.error('Error deleting production unit:', error)
      toast.error(error.data || 'There was an error deleting the production unit.')
    }
  }, [selectedUnit, deleteUnit, closeForm])

  return {
    productionUnits,
    allProductionUnits,
    isLoadingUnits,
    fetchError,
    availableYears,
    selectedYear,
    yearOptions,
    handleChangeYear,
    isFormOpen,
    isDeleteModalOpen,
    formType,
    selectedUnit,
    isSkippedPeriodModalOpen,
    formMethods,
    deleteFormMethods,
    openAddForm,
    openEditForm,
    openDeleteModal,
    closeForm,
    handleSubmit,
    handleConfirmedSubmit,
    closeSkippedPeriodModal,
    handleDelete,
    setFirstAvailablePeriodCallback,
    setFirstAvailableYearCallback,
    isSubmitting: isCreating || isUpdating,
    isDeleting,
    isFormValid: formMethods.formState.isValid,
  }
}
